import React, { useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  HiCheckCircle,
  HiOutlineClipboardList,
  HiOutlinePlusCircle,
  HiOutlineDocumentText,
  HiOutlineCalendar,
} from "react-icons/hi";
import { Button } from "@components";
import { useNavigate } from "react-router-dom";
import { modalSurface, Stack } from "@design-system";

interface OrderSuccessPopupProps {
  onClose: () => void;
  onPlanStops: () => void;
  onOpenDailyPlanner: () => void;
  onCreateNewOrder: () => void;
  orderId?: string;
}

export const OrderSuccessPopup: React.FC<OrderSuccessPopupProps> = ({
  onClose,
  onPlanStops,
  onOpenDailyPlanner,
  onCreateNewOrder,
  orderId,
}) => {
  const { t } = useTranslation(["orders"]);
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Håndter klikk utenfor modalen
  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    },
    [onClose],
  );

  // Legg til og fjern event listener for klikk
  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleOutsideClick]);

  // Håndter navigering til ordredetaljer
  const handleViewOrder = () => {
    if (orderId) {
      // Først lukk popupen
      onClose();

      // Bruk en timeout for å sikre at navigasjonen skjer etter at popupen er lukket
      setTimeout(() => {
        navigate(`/orders?id=${orderId}`);
      }, 100);
    } else {
      console.warn("Cannot view order: No order ID provided");
      // We'll still close the modal even if there's no ID
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]">
      <div
        ref={modalRef}
        className={`${modalSurface} p-8 max-w-md w-full transform transition-all animate-fadeIn`}
      >
        <div className="flex flex-col items-center">
          <div className="bg-green-100 dark:bg-green-900 p-4 rounded-full mb-6">
            <HiCheckCircle className="text-green-500 dark:text-green-400 h-16 w-16" />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-center dark:text-white">
            {t("orders:success.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 text-center">
            {t("orders:success.message")}
          </p>
          <Stack gap={4} className="w-full">
            <Button
              variant="success"
              onClick={onOpenDailyPlanner}
              icon={<HiOutlineCalendar className="mr-2 h-5 w-5" />}
              className="w-full py-3 sm:py-2.5"
            >
              {t("orders:success.open_daily_planner_beta")}
            </Button>

            <Button
              variant="secondary"
              onClick={onPlanStops}
              icon={<HiOutlineClipboardList className="mr-2 h-5 w-5" />}
              className="w-full py-3 sm:py-2.5"
            >
              {t("orders:success.open_classic_planner")}
            </Button>

            <div className="flex flex-row gap-4">
              <Button
                variant="secondary"
                onClick={handleViewOrder}
                icon={<HiOutlineDocumentText className="mr-2 h-5 w-5" />}
                className="w-1/2 py-3 sm:py-2.5"
                data-testid="view-order-button"
              >
                {t("orders:success.view_order", "View order")}
              </Button>

              <Button
                variant="secondary"
                onClick={onCreateNewOrder}
                icon={<HiOutlinePlusCircle className="mr-2 h-5 w-5" />}
                className="w-1/2 py-3 sm:py-2.5"
              >
                {t("orders:success.create_new_order")}
              </Button>
            </div>
          </Stack>
        </div>
      </div>
    </div>
  );
};

// Legg til en CSS-animasjon for fadeIn-effekten
const style = document.createElement("style");
style.innerHTML = `
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }
`;
document.head.appendChild(style);

export default OrderSuccessPopup;
